import { useEffect, useState } from 'react';
import axios from 'axios';
import { Egg, CircleDollarSign, ShoppingCart, Users, TrendingUp, Package } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatCurrency } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [productionData, setProductionData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currencySymbol } = useCurrency();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, productionRes, salesRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`, getAuthHeader()),
        axios.get(`${API}/reports/production?days=7`, getAuthHeader()),
        axios.get(`${API}/reports/sales?days=7`, getAuthHeader())
      ]);

      setStats(statsRes.data);
      setProductionData(productionRes.data.reverse());
      setSalesData(salesRes.data.reverse());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Today's Production",
      value: stats?.today_production || 0,
      unit: 'eggs',
      icon: Egg,
      color: 'bg-amber-100 text-amber-600',
      testId: 'today-production'
    },
    {
      title: "Today's Sales",
      value: `${currencySymbol}${formatCurrency(stats?.today_sales || 0)}`,
      icon: CircleDollarSign,
      color: 'bg-emerald-100 text-emerald-600',
      testId: 'today-sales'
    },
    {
      title: 'Pending Orders',
      value: stats?.pending_orders || 0,
      icon: ShoppingCart,
      color: 'bg-blue-100 text-blue-600',
      testId: 'pending-orders'
    },
    {
      title: 'Total Customers',
      value: stats?.customers_count || 0,
      icon: Users,
      color: 'bg-purple-100 text-purple-600',
      testId: 'total-customers'
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-lg text-slate-600 mt-2">
          Welcome back, {user?.name}! Here's your farm overview.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="card p-6" data-testid={stat.testId}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stat.value}
                  </p>
                  {stat.unit && (
                    <p className="text-sm text-slate-500 mt-1">{stat.unit}</p>
                  )}
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production Chart */}
        <div className="card p-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Weekly Production
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={productionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '12px' }} />
              <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Line type="monotone" dataKey="eggs_collected" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Sales Chart */}
        <div className="card p-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Weekly Sales
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '12px' }} />
              <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="total_amount" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6 bg-gradient-to-br from-white to-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <h4 className="text-sm font-semibold text-slate-900">Total Production</h4>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats?.total_production || 0}</p>
          <p className="text-sm text-slate-500 mt-1">eggs collected</p>
        </div>

        <div className="card p-6 bg-gradient-to-br from-white to-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <CircleDollarSign className="w-5 h-5 text-emerald-600" />
            <h4 className="text-sm font-semibold text-slate-900">Total Sales</h4>
          </div>
          <p className="text-2xl font-bold text-slate-900">{currencySymbol}{formatCurrency(stats?.total_sales_amount || 0)}</p>
          <p className="text-sm text-slate-500 mt-1">{stats?.total_eggs_sold || 0} eggs sold</p>
        </div>

        <div className="card p-6 bg-gradient-to-br from-white to-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <Package className="w-5 h-5 text-amber-600" />
            <h4 className="text-sm font-semibold text-slate-900">Eggs in Stock</h4>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats?.eggs_in_stock || 0}</p>
          <p className="text-sm text-slate-500 mt-1">available eggs</p>
        </div>
      </div>

      {/* Feed Stock */}
      {stats?.feed_stock && Object.keys(stats.feed_stock).length > 0 && (
        <div className="card p-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Feed Inventory
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(stats.feed_stock).map(([feedType, data]) => (
              <div key={feedType} className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-sm font-medium text-slate-900 capitalize">{feedType}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{(data?.total_stock || 0).toFixed(1)} kg</p>
                <p className="text-xs text-slate-500 mt-1">Value: {currencySymbol}{formatCurrency(data?.total_value || 0)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;